import {
  Box,
  Grid,
  Link,
  List,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendContext } from "contexts/QuickSend";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useRef, useState } from "react";
import {
  BentoBrandTier,
  BentoContact,
  BentoContactSource,
  NewBentoContact,
  UpgradeDialogSource,
} from "schemas/dashboard";

import ExclusiveBrand from "components/ExclusiveBrand";
import { EXCLUSIVE_CONTACTED_THRESHOLD } from "constants/influencer";
import {
  accessToPartnershipData,
  contactAskForRates,
  contactDiscussedCollab,
  contactReplied,
  contactShowedInterest,
} from "utils/influencer";
import { trackEvent } from "utils/tracking";
import { usePageSize } from "utils/usePageSize";

import ContactListItem from "./ContactListItem";
import ExclusiveContact from "./ExclusiveContact";
import styles from "./styles";

interface ContactScoreProps {
  contacts?: (BentoContact | NewBentoContact)[];
}

const PAYWALL_PREVIEW_OPTIONS = [
  "Asked a user for rates",
  "Discussed a specific opportunity with a user",
  "Showed general interest in working with creators",
];

export default function ContactScore({ contacts }: ContactScoreProps) {
  const { isOnboardingPage } = usePageSize();
  const { bentoContact, bentoBrand } = useContext(QuickSendContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { emailBrandDialogOpen } = useContext(OnboardingDialogsContext);

  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
  );
  const hasAccessToImportedContacts = currentOrg?.allowShareContacts;

  const displayScore = subscription?.isSubscribed || hasAccessToPartnershipData;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleViewScore = () => {
    trackEvent("View Score Button Clicked");
    setUpgradeDialogSource(UpgradeDialogSource.reply_score);
  };

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!bentoContact?.bestResponseType && !bentoContact?.lastSentToAt) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const handlePopperMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handlePopperMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const renderScoreAnnotation = () => {
    if (displayScore && bentoContact) {
      return (
        <>
          {bentoContact?.sourcedFromUserInbox && (
            <Tooltip
              title={
                "This is an exclusive contact (who are 2-3 times more likely to respond)."
              }
              enterTouchDelay={0}
            >
              <Box component="span" sx={{ fontSize: 12, mx: 1 }}>
                💎
              </Box>
            </Tooltip>
          )}
          {hasAccessToImportedContacts &&
            bentoContact?.source === BentoContactSource.gmail_import && (
              <Tooltip
                title={
                  currentOrg?.isVaUser
                    ? "This is an exclusive contact (you see these contacts because you are on the Virtual Assistant Plan)"
                    : "This is an imported contact (you see these contacts because you agreed to share your contacts when syncing your email)"
                }
                enterTouchDelay={0}
              >
                <Box
                  component="span"
                  sx={{ fontSize: 12, mx: 1, color: "success.main" }}
                  className="fa-solid fa-star"
                />
              </Tooltip>
            )}
          <Box
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{ ml: 0.5 }}
          >
            {contactReplied(bentoContact) ? (
              <Box sx={{ color: green[500] }}>
                <Box component="i" className="fa-regular fa-check-double" />
              </Box>
            ) : bentoContact.lastSentToAt ? (
              <Box sx={{ color: grey[400] }}>
                <Box component="i" className="fa-regular fa-check-double" />
              </Box>
            ) : (
              <Box></Box>
            )}
            <Popper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              sx={{
                zIndex: 1,
              }}
              role={undefined}
              placement="bottom"
              disablePortal
            >
              <Paper
                onMouseEnter={handlePopperMouseEnter}
                onMouseLeave={handlePopperMouseLeave}
                sx={{ maxWidth: "250px", px: 1, mr: 1, py: 0.5 }}
              >
                <List dense disablePadding>
                  <ContactListItem
                    isActive
                    text="Email verified (not bounced)"
                  />
                  <ContactListItem
                    isActive={contactReplied(bentoContact)}
                    text="Replied to a user on platform"
                  />
                  {hasAccessToPartnershipData && (
                    <>
                      {contactAskForRates(bentoContact) ? (
                        <ContactListItem
                          isActive
                          text="Asked a user for rates"
                        />
                      ) : contactDiscussedCollab(bentoContact) ? (
                        <ContactListItem
                          isActive
                          text="Discussed a specific opportunity with a user"
                        />
                      ) : contactShowedInterest(bentoContact) ? (
                        <ContactListItem
                          isActive
                          text="Showed general interest in working with creators"
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </List>
                {!hasAccessToPartnershipData && (
                  <>
                    <Typography sx={{ mt: 1 }} gutterBottom>
                      <Link
                        component="button"
                        sx={{
                          fontSize: 12,
                          color: green[600],
                        }}
                        onClick={() => {
                          setUpgradeDialogSource(
                            UpgradeDialogSource.access_partnership_data,
                          );
                        }}
                      >
                        <Box
                          component="span"
                          className="fa-solid fa-lock-keyhole"
                          sx={{ mr: 1 }}
                        />
                        <strong>Upgrade For:</strong>
                      </Link>
                    </Typography>
                    <List dense disablePadding>
                      {PAYWALL_PREVIEW_OPTIONS.map((paywallOption) => (
                        <ContactListItem
                          key={paywallOption}
                          isActive={false}
                          isPaywall
                          text={paywallOption}
                        />
                      ))}
                    </List>
                  </>
                )}
              </Paper>
            </Popper>
          </Box>
        </>
      );
    } else if (
      bentoBrand?.tier === BentoBrandTier.tier_one ||
      (bentoBrand?.contactedRecentlyAmount &&
        bentoBrand?.contactedRecentlyAmount >= EXCLUSIVE_CONTACTED_THRESHOLD)
    ) {
      return (
        <ExclusiveBrand
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          selectedBrand={bentoBrand}
        />
      );
    } else if (bentoContact?.sourcedFromUserInbox) {
      return <ExclusiveContact anchorEl={anchorEl} setAnchorEl={setAnchorEl} />;
    } else {
      return (
        <>
          {hasAccessToImportedContacts &&
            bentoContact?.source === BentoContactSource.gmail_import && (
              <Tooltip
                title={
                  currentOrg?.isVaUser
                    ? "This is an exclusive contact (you see these contacts because you are on the Virtual Assistant Plan)"
                    : "This is an imported contact (you see these contacts because you agreed to share your contacts when syncing your email)"
                }
                enterTouchDelay={0}
              >
                <Box
                  component="span"
                  sx={{ fontSize: 12, mx: 1, color: "success.main" }}
                  className="fa-solid fa-star"
                />
              </Tooltip>
            )}
          <Tooltip
            enterTouchDelay={0}
            title="View how likely this contact will respond to you with Bento's 'Reply Score' feature. Upgrade to unlock"
          >
            <Link sx={styles.viewScore} onClick={handleViewScore}>
              Reply Score
            </Link>
          </Tooltip>
        </>
      );
    }
  };

  return (
    <>
      {contacts &&
        contacts?.length > 0 &&
        bentoContact &&
        !isOnboardingPage &&
        !emailBrandDialogOpen &&
        bentoContact?.score !== undefined && (
          <Grid item xs="auto" container alignItems="center">
            {renderScoreAnnotation()}
          </Grid>
        )}
    </>
  );
}
